// Paneon Pets API Service
// Uses Supabase as the backend

import { supabase, type DbPet, type DbFeeding, type DbWeightRecord } from '../config/supabase';

// Types
export interface Pet {
  id: string;
  user_id: string;
  name: string;
  species: string;
  breed?: string;
  birth_date?: string;
  weight_kg?: number;
  photo_url?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
}

export interface Feeding {
  id: string;
  pet_id: string;
  user_id: string;
  feeding_type: string;
  food_type: string;
  food_name?: string;
  brand?: string;
  amount_grams?: number;
  calories?: number;
  notes?: string;
  fed_at: number; // Unix timestamp for compatibility
  created_at: string;
  pet_name?: string;
}

export interface WeightRecord {
  id: string;
  pet_id: string;
  weight_kg: number;
  measured_at: string;
  notes?: string;
}

export interface UserPreferences {
  theme_id: string;
  notification_enabled: boolean;
  daily_reminder_time: string;
  weight_unit: string;
  language: string;
  dashboard_widgets?: Record<string, unknown>;
  widget_order?: string[];
  auto_compact_grid: boolean;
}

// Helper to get current user ID from Firebase Auth
async function getCurrentUserId(): Promise<string> {
  const storedUser = localStorage.getItem('paneon_user');
  if (storedUser) {
    const user = JSON.parse(storedUser);
    return user.uid;
  }
  throw new Error('Not authenticated');
}

// Helper to generate UUID
function generateId(): string {
  return crypto.randomUUID();
}

// Convert DB pet to API pet
function mapDbPetToApi(dbPet: DbPet): Pet {
  return {
    id: dbPet.id,
    user_id: dbPet.user_id,
    name: dbPet.name,
    species: dbPet.species,
    breed: dbPet.breed || undefined,
    birth_date: dbPet.birth_date || undefined,
    weight_kg: dbPet.weight_kg || undefined,
    photo_url: dbPet.photo_url || undefined,
    notes: dbPet.notes || undefined,
    is_active: dbPet.is_active,
    created_at: dbPet.created_at,
  };
}

// Convert DB feeding to API feeding
function mapDbFeedingToApi(dbFeeding: DbFeeding): Feeding {
  return {
    id: dbFeeding.id,
    pet_id: dbFeeding.pet_id,
    user_id: dbFeeding.user_id,
    feeding_type: dbFeeding.feeding_type,
    food_type: dbFeeding.food_type,
    food_name: dbFeeding.food_name || undefined,
    brand: dbFeeding.brand || undefined,
    amount_grams: dbFeeding.amount_grams || undefined,
    calories: dbFeeding.calories || undefined,
    notes: dbFeeding.notes || undefined,
    fed_at: Math.floor(new Date(dbFeeding.fed_at).getTime() / 1000),
    created_at: dbFeeding.created_at,
    pet_name: dbFeeding.pets?.name,
  };
}

// Pet API
export const petApi = {
  async list(): Promise<Pet[]> {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('pets')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []).map(mapDbPetToApi);
  },

  async get(id: string): Promise<Pet> {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('pets')
      .select('*')
      .eq('id', id)
      .eq('user_id', userId)
      .single();

    if (error) throw new Error(error.message);
    return mapDbPetToApi(data);
  },

  async create(pet: {
    name: string;
    species: string;
    breed?: string;
    birth_date?: string;
    weight_kg?: number;
    photo_url?: string;
    notes?: string;
  }): Promise<Pet> {
    const userId = await getCurrentUserId();
    const id = generateId();

    const { data, error } = await supabase
      .from('pets')
      .insert({
        id,
        user_id: userId,
        name: pet.name,
        species: pet.species,
        breed: pet.breed || null,
        birth_date: pet.birth_date || null,
        weight_kg: pet.weight_kg || null,
        photo_url: pet.photo_url || null,
        notes: pet.notes || null,
        is_active: true,
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    return mapDbPetToApi(data);
  },

  async update(id: string, pet: Partial<Pet>): Promise<Pet> {
    const userId = await getCurrentUserId();

    const updateData: Record<string, unknown> = {};
    if (pet.name !== undefined) updateData.name = pet.name;
    if (pet.species !== undefined) updateData.species = pet.species;
    if (pet.breed !== undefined) updateData.breed = pet.breed || null;
    if (pet.birth_date !== undefined) updateData.birth_date = pet.birth_date || null;
    if (pet.weight_kg !== undefined) updateData.weight_kg = pet.weight_kg || null;
    if (pet.photo_url !== undefined) updateData.photo_url = pet.photo_url || null;
    if (pet.notes !== undefined) updateData.notes = pet.notes || null;

    const { data, error } = await supabase
      .from('pets')
      .update(updateData)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return mapDbPetToApi(data);
  },

  async delete(id: string): Promise<{ success: boolean }> {
    const userId = await getCurrentUserId();

    // Soft delete
    const { error } = await supabase
      .from('pets')
      .update({ is_active: false })
      .eq('id', id)
      .eq('user_id', userId);

    if (error) throw new Error(error.message);
    return { success: true };
  },
};

// Feeding API
export const feedingApi = {
  async list(params?: {
    pet_id?: string;
    limit?: number;
    offset?: number;
    from?: number;
    to?: number;
  }): Promise<Feeding[]> {
    const userId = await getCurrentUserId();

    let query = supabase
      .from('feedings')
      .select('*, pets(name)')
      .eq('user_id', userId)
      .order('fed_at', { ascending: false });

    if (params?.pet_id) {
      query = query.eq('pet_id', params.pet_id);
    }

    if (params?.from) {
      const fromDate = new Date(params.from * 1000).toISOString();
      query = query.gte('fed_at', fromDate);
    }

    if (params?.to) {
      const toDate = new Date(params.to * 1000).toISOString();
      query = query.lte('fed_at', toDate);
    }

    if (params?.limit) {
      query = query.limit(params.limit);
    }

    if (params?.offset) {
      query = query.range(params.offset, params.offset + (params.limit || 50) - 1);
    }

    const { data, error } = await query;

    if (error) throw new Error(error.message);
    return (data || []).map(mapDbFeedingToApi);
  },

  async create(feeding: {
    pet_id: string;
    feeding_type: string;
    food_type: string;
    food_name?: string;
    brand?: string;
    amount_grams?: number;
    calories?: number;
    notes?: string;
    fed_at?: number;
  }): Promise<Feeding> {
    const userId = await getCurrentUserId();
    const id = generateId();
    const fedAt = feeding.fed_at
      ? new Date(feeding.fed_at * 1000).toISOString()
      : new Date().toISOString();

    const { data, error } = await supabase
      .from('feedings')
      .insert({
        id,
        user_id: userId,
        pet_id: feeding.pet_id,
        feeding_type: feeding.feeding_type,
        food_type: feeding.food_type,
        food_name: feeding.food_name || null,
        brand: feeding.brand || null,
        amount_grams: feeding.amount_grams || null,
        calories: feeding.calories || null,
        notes: feeding.notes || null,
        fed_at: fedAt,
      })
      .select('*, pets(name)')
      .single();

    if (error) throw new Error(error.message);
    return mapDbFeedingToApi(data);
  },

  async delete(id: string): Promise<{ success: boolean }> {
    const userId = await getCurrentUserId();

    const { error } = await supabase.from('feedings').delete().eq('id', id).eq('user_id', userId);

    if (error) throw new Error(error.message);
    return { success: true };
  },
};

// Weight API
export const weightApi = {
  async list(petId: string, limit?: number): Promise<WeightRecord[]> {
    const userId = await getCurrentUserId();

    let query = supabase
      .from('weight_records')
      .select('*')
      .eq('pet_id', petId)
      .eq('user_id', userId)
      .order('measured_at', { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) throw new Error(error.message);

    return (data || []).map((r: DbWeightRecord) => ({
      id: r.id,
      pet_id: r.pet_id,
      weight_kg: r.weight_kg,
      measured_at: r.measured_at,
      notes: r.notes || undefined,
    }));
  },

  async create(record: {
    pet_id: string;
    weight_kg: number;
    measured_at?: string;
    notes?: string;
  }): Promise<WeightRecord> {
    const userId = await getCurrentUserId();
    const id = generateId();

    const { data, error } = await supabase
      .from('weight_records')
      .insert({
        id,
        user_id: userId,
        pet_id: record.pet_id,
        weight_kg: record.weight_kg,
        measured_at: record.measured_at || new Date().toISOString().split('T')[0],
        notes: record.notes || null,
      })
      .select()
      .single();

    if (error) throw new Error(error.message);

    return {
      id: data.id,
      pet_id: data.pet_id,
      weight_kg: data.weight_kg,
      measured_at: data.measured_at,
      notes: data.notes || undefined,
    };
  },
};

// Preferences API
export const preferencesApi = {
  async get(): Promise<UserPreferences> {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw new Error(error.message);
    }

    // Return defaults if not found
    if (!data) {
      return {
        theme_id: 'paneon',
        notification_enabled: true,
        daily_reminder_time: '08:00',
        weight_unit: 'kg',
        language: 'de',
        auto_compact_grid: false,
      };
    }

    return {
      theme_id: data.theme_id,
      notification_enabled: data.notification_enabled,
      daily_reminder_time: data.daily_reminder_time,
      weight_unit: data.weight_unit,
      language: data.language,
      dashboard_widgets: data.dashboard_widgets_v2 as Record<string, unknown> | undefined,
      widget_order: data.widget_order,
      auto_compact_grid: data.auto_compact_grid,
    };
  },

  async update(prefs: Partial<UserPreferences>): Promise<UserPreferences> {
    const userId = await getCurrentUserId();

    const updateData: Record<string, unknown> = { user_id: userId };
    if (prefs.theme_id !== undefined) updateData.theme_id = prefs.theme_id;
    if (prefs.notification_enabled !== undefined)
      updateData.notification_enabled = prefs.notification_enabled;
    if (prefs.daily_reminder_time !== undefined)
      updateData.daily_reminder_time = prefs.daily_reminder_time;
    if (prefs.weight_unit !== undefined) updateData.weight_unit = prefs.weight_unit;
    if (prefs.language !== undefined) updateData.language = prefs.language;
    if (prefs.dashboard_widgets !== undefined)
      updateData.dashboard_widgets_v2 = prefs.dashboard_widgets;
    if (prefs.widget_order !== undefined) updateData.widget_order = prefs.widget_order;
    if (prefs.auto_compact_grid !== undefined)
      updateData.auto_compact_grid = prefs.auto_compact_grid;

    const { data, error } = await supabase
      .from('user_preferences')
      .upsert(updateData)
      .select()
      .single();

    if (error) throw new Error(error.message);

    return {
      theme_id: data.theme_id,
      notification_enabled: data.notification_enabled,
      daily_reminder_time: data.daily_reminder_time,
      weight_unit: data.weight_unit,
      language: data.language,
      dashboard_widgets: data.dashboard_widgets_v2 as Record<string, unknown> | undefined,
      widget_order: data.widget_order,
      auto_compact_grid: data.auto_compact_grid,
    };
  },
};

// User API
export const userApi = {
  async me(): Promise<{
    id: string;
    email: string;
    name: string | null;
    avatar_url: string | null;
  }> {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('users')
      .select('id, email, name, avatar_url')
      .eq('id', userId)
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  async updateProfile(profile: { name?: string; avatar_url?: string }): Promise<void> {
    const userId = await getCurrentUserId();

    const { error } = await supabase.from('users').update(profile).eq('id', userId);

    if (error) throw new Error(error.message);
  },
};

// Health check (for compatibility)
export const healthApi = {
  async check(): Promise<{ status: string; timestamp: string; version: string }> {
    // Simple Supabase connectivity check
    const { error } = await supabase.from('users').select('count').limit(0);

    return {
      status: error ? 'unhealthy' : 'healthy',
      timestamp: new Date().toISOString(),
      version: '0.1.0',
    };
  },
};
